/**
 * Shared sign-in surface consumed by both:
 *   • Edge.Client SPA at /login (self-hosted; cloud Edge bundles this route
 *     out via vite-plugin-pages exclude — CF Pages owns /login in cloud)
 *   • inspire-login CF Pages bundle (cloud only)
 *
 * Standalone by design — no dependency on react-router-dom, @servicestack/react,
 * or ServiceStack DTOs. Navigation happens via `window.location.href`; auth
 * state is re-derived from /api/identity/me on mount. Both bundles import
 * this component verbatim and wrap it with their own thin entry point.
 *
 * Browser sessions are ASP.NET Core Identity cookies (`inspire-session`),
 * minted by SignInManager on Edge after authenticate succeeds. JWTs in this
 * flow are short-lived signed claims only — locate token + MFA challenge
 * token — never the browser session cookie itself.
 */

import {
    type ReactNode,
    type SyntheticEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import { motion, AnimatePresence } from 'motion/react'
import {
    authenticate as apiAuthenticate,
    buildOidcAuthorizeUrl,
    buildSamlRedirectUrl,
    changePassword,
    getCapabilities,
    isLocateChallenge,
    locate as apiLocate,
    logout as apiLogout,
    me as apiMe,
    resolveSso,
    verifyMfa as apiVerifyMfa,
    type LocateResponseOk,
} from '@/lib/identity-api'

// ─── Props ────────────────────────────────────────────────────

export interface SignInProps {
    /** Where to send the user after successful sign-in. Default '/'. */
    redirectTo?: string

    /**
     * Optional override for the "Forgot password" + "Sign out" / etc. links
     * to navigate. Defaults to `window.location.href = url`. Edge.Client may
     * override with `react-router-dom`'s navigate to avoid the full-reload
     * flicker on intra-app links. The post-sign-in destination always uses
     * `window.location.href` regardless so the auth boundary is consistent.
     */
    navigate?: (url: string) => void
}

// ─── State machine ────────────────────────────────────────────

type Stage =
    | { phase: 'loading' }
    | { phase: 'email' }
    | {
          phase: 'org-picker'
          email: string
          locateToken: string
          choices: { region: string; label?: string }[]
      }
    | {
          phase: 'password'
          email: string
          locateToken: string | null
          /**
           * Set when the locate step returned multi-region matches and
           * the user picked one in the `org-picker` stage. Passed to
           * authenticate so the Worker knows which region's credential
           * store to validate against. Single-region matches leave this
           * undefined — the locateToken carries the only region.
           */
          region?: string
          branding: { tenantName: string; logoUrl: string } | null
      }
    | { phase: 'mfa'; mfaToken: string; branding: { tenantName: string; logoUrl: string } | null }
    | { phase: 'sso-redirecting'; tenantName: string | null }
    | { phase: 'force-change'; redirectAfter: string }
    | { phase: 'done'; redirectAfter: string }

/**
 * Friendly labels for the region picker. Region codes follow Fly's
 * three-letter convention (IATA airport codes); the picker shows
 * city / region names instead because end users don't speak airport.
 * Add new entries as regions come online; the fallback uppercases
 * the raw code so a new region is at least intelligible without
 * needing a client redeploy.
 */
const REGION_LABELS: Record<string, string> = {
    lhr: 'London, UK',
    iad: 'Washington, US',
    syd: 'Sydney, AU',
    fra: 'Frankfurt, DE',
    sin: 'Singapore',
    nrt: 'Tokyo, JP',
    gru: 'São Paulo, BR',
}

function regionLabel(region: string, supplied?: string): string {
    if (supplied && supplied.length > 0) return supplied
    return REGION_LABELS[region.toLowerCase()] ?? region.toUpperCase()
}

interface FlowError {
    message: string
}

// ─── Theme detection ─────────────────────────────────────────
// Reactively reads the `.dark` class off <html>. The class is set by
// the inline dark-mode-detection script in both bundles' index.html
// before paint, then can flip later via a user toggle. Components
// that need to swap brand assets (logo SVG variants) or recolour
// non-CSS-driven surfaces (the canvas in AnimatedBackground) consume
// this rather than reading `document.documentElement.classList` ad-hoc.

function useIsDark(): boolean {
    const [isDark, setIsDark] = useState(() =>
        typeof document !== 'undefined' && document.documentElement.classList.contains('dark'),
    )
    useEffect(() => {
        if (typeof document === 'undefined') return
        const html = document.documentElement
        const update = () => setIsDark(html.classList.contains('dark'))
        update()
        const observer = new MutationObserver(update)
        observer.observe(html, { attributes: true, attributeFilter: ['class'] })
        return () => observer.disconnect()
    }, [])
    return isDark
}

// ─── Animated background (inlined; runs in both bundles) ──────

function AnimatedBackground() {
    const canvasRef = useRef<HTMLCanvasElement>(null)

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        if (!ctx) return

        let animationId = 0
        let time = 0
        let ww = window.innerWidth
        let wh = window.innerHeight

        const resize = () => {
            ww = window.innerWidth
            wh = window.innerHeight
            canvas.width = ww * devicePixelRatio
            canvas.height = wh * devicePixelRatio
            canvas.style.width = ww + 'px'
            canvas.style.height = wh + 'px'
        }
        resize()
        window.addEventListener('resize', resize)

        const strands = [
            { yBase: 0.15, amplitude: 0.12, frequency: 1.2, speed: 0.4, width: 180, color: [140, 40, 100], phase: 0, opacity: 0.5 },
            { yBase: 0.35, amplitude: 0.18, frequency: 0.8, speed: 0.3, width: 220, color: [80, 60, 180], phase: 1.5, opacity: 0.4 },
            { yBase: 0.55, amplitude: 0.14, frequency: 1.0, speed: 0.35, width: 160, color: [200, 60, 100], phase: 3.0, opacity: 0.45 },
            { yBase: 0.75, amplitude: 0.16, frequency: 0.9, speed: 0.25, width: 200, color: [50, 50, 160], phase: 4.5, opacity: 0.35 },
            { yBase: 0.25, amplitude: 0.20, frequency: 0.7, speed: 0.28, width: 240, color: [160, 30, 120], phase: 2.0, opacity: 0.3 },
            { yBase: 0.65, amplitude: 0.10, frequency: 1.4, speed: 0.45, width: 140, color: [120, 50, 160], phase: 5.0, opacity: 0.4 },
            { yBase: 0.45, amplitude: 0.22, frequency: 0.6, speed: 0.2, width: 260, color: [180, 50, 80], phase: 1.0, opacity: 0.25 },
            { yBase: 0.85, amplitude: 0.08, frequency: 1.6, speed: 0.5, width: 120, color: [100, 40, 140], phase: 3.5, opacity: 0.45 },
        ]

        const draw = () => {
            time += 0.002
            const isDark =
                typeof document !== 'undefined' && document.documentElement.classList.contains('dark')

            ctx.save()
            ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0)

            ctx.fillStyle = isDark ? '#0f0a12' : '#faf7fb'
            ctx.fillRect(0, 0, ww, wh)

            // Light-mode orbs are dialled up vs dark because they're the
            // primary source of brand colour on the otherwise off-white
            // canvas — dark mode has the strands doing more of the work
            // on the already-saturated background. Three orbs in light
            // mode form a soft tri-tone gradient (pompadour / hibiscus /
            // stratos), matching Inspire's brand palette without painting
            // a heavy gradient overlay.
            const ambientOrbs = isDark
                ? [
                    { x: 0.25, y: 0.3, r: 400, color: 'rgba(140,40,100,0.12)' },
                    { x: 0.75, y: 0.7, r: 350, color: 'rgba(80,60,180,0.10)' },
                ]
                : [
                    { x: 0.22, y: 0.28, r: 520, color: 'rgba(180,60,130,0.16)' },
                    { x: 0.78, y: 0.32, r: 460, color: 'rgba(100,80,200,0.13)' },
                    { x: 0.50, y: 0.85, r: 540, color: 'rgba(220,100,140,0.10)' },
                ]
            for (const orb of ambientOrbs) {
                const g = ctx.createRadialGradient(orb.x * ww, orb.y * wh, 0, orb.x * ww, orb.y * wh, orb.r)
                g.addColorStop(0, orb.color)
                g.addColorStop(1, 'transparent')
                ctx.fillStyle = g
                ctx.fillRect(0, 0, ww, wh)
            }

            ctx.globalCompositeOperation = isDark ? 'lighter' : 'multiply'

            for (const strand of strands) {
                const steps = 120
                const points: { x: number; y: number }[] = []
                for (let i = 0; i <= steps; i++) {
                    const t = i / steps
                    const x = t * ww * 1.4 - ww * 0.2
                    const baseY = strand.yBase * wh
                    const wave1 = Math.sin(t * Math.PI * 2 * strand.frequency + time * strand.speed + strand.phase) * strand.amplitude * wh
                    const wave2 = Math.sin(t * Math.PI * 3.7 * strand.frequency * 0.5 + time * strand.speed * 0.7 + strand.phase + 1) * strand.amplitude * wh * 0.3
                    const wave3 = Math.cos(t * Math.PI * 1.3 + time * strand.speed * 1.2 + strand.phase * 0.5) * strand.amplitude * wh * 0.15
                    const y = baseY + wave1 + wave2 + wave3
                    points.push({ x, y })
                }
                const halfWidth = strand.width * (ww / 1600)
                const [r, g, b] = strand.color
                // Light mode uses multiply blend over an off-white
                // background — at strand.opacity * 1.2 the strands look
                // like sharp ribbons against the orbs and compete with
                // the centred card for visual hierarchy. 0.55 tones them
                // back to a colour wash that supports the orbs without
                // distracting from the form. Dark mode keeps the
                // brighter 0.7 since it uses lighter blend over a dark
                // bg — different colour-mix maths.
                const alpha = isDark ? strand.opacity * 0.7 : strand.opacity * 0.55
                const top: { x: number; y: number }[] = []
                const bottom: { x: number; y: number }[] = []
                for (let i = 0; i < points.length; i++) {
                    const p = points[i]!
                    const prev = points[Math.max(0, i - 1)]!
                    const next = points[Math.min(points.length - 1, i + 1)]!
                    const dx = next.x - prev.x
                    const dy = next.y - prev.y
                    const len = Math.sqrt(dx * dx + dy * dy) || 1
                    const nx = -dy / len
                    const ny = dx / len
                    const taper = Math.sin((i / points.length) * Math.PI)
                    const w = halfWidth * taper * (0.8 + 0.2 * Math.sin(time * 0.5 + i * 0.05 + strand.phase))
                    top.push({ x: p.x + nx * w, y: p.y + ny * w })
                    bottom.push({ x: p.x - nx * w, y: p.y - ny * w })
                }
                ctx.beginPath()
                ctx.moveTo(top[0]!.x, top[0]!.y)
                for (let i = 1; i < top.length; i++) ctx.lineTo(top[i]!.x, top[i]!.y)
                for (let i = bottom.length - 1; i >= 0; i--) ctx.lineTo(bottom[i]!.x, bottom[i]!.y)
                ctx.closePath()
                const grad = ctx.createLinearGradient(0, 0, ww, 0)
                grad.addColorStop(0, `rgba(${r},${g},${b},0)`)
                grad.addColorStop(0.2, `rgba(${r},${g},${b},${alpha * 0.6})`)
                grad.addColorStop(0.5, `rgba(${r},${g},${b},${alpha})`)
                grad.addColorStop(0.8, `rgba(${r},${g},${b},${alpha * 0.6})`)
                grad.addColorStop(1, `rgba(${r},${g},${b},0)`)
                ctx.fillStyle = grad
                ctx.filter = `blur(${Math.round(strand.width * 0.15)}px)`
                ctx.fill()
                ctx.filter = 'none'
            }

            ctx.globalCompositeOperation = 'source-over'
            ctx.globalAlpha = isDark ? 0.025 : 0.015
            ctx.fillStyle = isDark ? '#fff' : '#000'
            for (let i = 0; i < 600; i++) ctx.fillRect(Math.random() * ww, Math.random() * wh, 1, 1)
            ctx.globalAlpha = 1

            ctx.restore()
            animationId = requestAnimationFrame(draw)
        }

        draw()
        return () => {
            cancelAnimationFrame(animationId)
            window.removeEventListener('resize', resize)
        }
    }, [])

    return <canvas ref={canvasRef} className="fixed inset-0 -z-20" aria-hidden="true" />
}

function GridOverlay() {
    return (
        <svg
            className="fixed inset-0 -z-10 w-full h-full opacity-[var(--grid-opacity)]"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            <defs>
                <pattern id="signin-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <circle cx="20" cy="20" r="0.8" className="fill-foreground/30" />
                </pattern>
                <radialGradient id="signin-grid-fade" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="white" stopOpacity="1" />
                    <stop offset="70%" stopColor="white" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="white" stopOpacity="0" />
                </radialGradient>
                <mask id="signin-grid-mask">
                    <rect width="100%" height="100%" fill="url(#signin-grid-fade)" />
                </mask>
            </defs>
            <rect width="100%" height="100%" fill="url(#signin-grid)" mask="url(#signin-grid-mask)" />
        </svg>
    )
}

// ─── Helpers ──────────────────────────────────────────────────

function parseRedirect(): string | undefined {
    if (typeof window === 'undefined') return undefined
    const params = new URLSearchParams(window.location.search)
    const raw = params.get('redirect')
    if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return undefined
    return raw
}

function redirectHashKey(redirect: string | undefined): string {
    return redirect && redirect.startsWith('/') ? redirect : '/'
}

/** UI-facing password-policy hint. Mirrors `Edge/Program.cs` Identity
 *  defaults (length 8, upper, lower, digit, symbol). The server is
 *  authoritative — admins can tune the policy at runtime — but the hint
 *  saves users a round-trip on the common case. */
const PASSWORD_POLICY_HINT = 'At least 8 characters, with upper, lower, a digit, and a symbol.'

/** Returns null on pass, or a single human message on first-fail.
 *  Order chosen to match the helper text users see above. */
function validatePasswordPolicy(pw: string): string | null {
    if (pw.length < 8) return 'Use at least 8 characters.'
    if (!/[a-z]/.test(pw)) return 'Include a lowercase letter.'
    if (!/[A-Z]/.test(pw)) return 'Include an uppercase letter.'
    if (!/\d/.test(pw)) return 'Include a digit.'
    if (!/[^A-Za-z0-9]/.test(pw)) return 'Include a symbol (e.g. !@#$%).'
    return null
}

// ─── Component ────────────────────────────────────────────────

export function SignIn({ redirectTo, navigate }: SignInProps = {}): ReactNode {
    const isDark = useIsDark()
    const initialRedirect = redirectTo ?? parseRedirect() ?? '/'
    const externalNavigate = useCallback(
        (url: string) => {
            if (navigate) navigate(url)
            else window.location.href = url
        },
        [navigate],
    )

    // Auth state — established by /api/identity/me on mount and after sign-in
    const [stage, setStage] = useState<Stage>({ phase: 'loading' })
    const [error, setError] = useState<FlowError | null>(null)

    // Inputs
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [mfaCode, setMfaCode] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')

    // Tenant branding overlay (set by ResolveSsoProvider OR locate)
    const [tenantLogoUrl, setTenantLogoUrl] = useState<string | null>(null)
    const [tenantName, setTenantName] = useState<string | null>(null)

    // In-flight flags
    const [busy, setBusy] = useState(false)
    const [resolving, setResolving] = useState(false)
    const [passwordSaving, setPasswordSaving] = useState(false)

    // Platform capabilities → drives "Forgot password" link visibility
    const [emailEnabled, setEmailEnabled] = useState(false)

    // MFA modal
    const [mfaModalOpen, setMfaModalOpen] = useState(false)
    const [mfaSubmitting, setMfaSubmitting] = useState(false)
    const newPasswordRef = useRef<HTMLInputElement>(null)
    const mfaReturnFocusRef = useRef<HTMLElement | null>(null)

    // ─── Mount: rehydrate auth state + capabilities ──────────

    useEffect(() => {
        let cancelled = false
        ;(async () => {
            try {
                const meResult = await apiMe()
                if (cancelled) return
                if (meResult && meResult.userId) {
                    // Already signed in — redirect away from /login. If
                    // mustChangePassword, show the inline force-change form
                    // instead.
                    if (meResult.mustChangePassword) {
                        setStage({ phase: 'force-change', redirectAfter: initialRedirect })
                        return
                    }
                    externalNavigate(initialRedirect)
                    return
                }
            } catch {
                // Not signed in — fall through to email step.
            }
            if (!cancelled) setStage({ phase: 'email' })
        })()
        ;(async () => {
            try {
                const caps = await getCapabilities()
                if (!cancelled) setEmailEnabled(caps.emailEnabled === true)
            } catch {
                // Capabilities probe is best-effort; default emailEnabled=false
                // already hides the forgot-password link (fail closed).
            }
        })()
        return () => {
            cancelled = true
        }
    }, [externalNavigate, initialRedirect])

    // ─── Email submit: SSO discovery + locate ────────────────

    const onEmailSubmit = useCallback(
        async (e: SyntheticEvent<HTMLFormElement>) => {
            e.preventDefault()
            const trimmed = email.trim()
            if (!trimmed) {
                setError({ message: 'Enter your work email to continue.' })
                return
            }
            setError(null)
            setResolving(true)
            try {
                // SSO discovery: ResolveSsoProvider tells us if the email's
                // tenant has OIDC/SAML configured. We prefer OIDC when both
                // present (shorter round-trip, smaller attack surface).
                let didSsoRedirect = false
                try {
                    const sso = await resolveSso(trimmed)
                    if (sso.found && sso.tenantSlug) {
                        if (sso.logoUrl) {
                            setTenantLogoUrl(sso.logoUrl)
                            setTenantName(sso.tenantName ?? null)
                        }
                        const providers = sso.providers ?? []
                        if (providers.includes('oidc')) {
                            setStage({ phase: 'sso-redirecting', tenantName: sso.tenantName ?? null })
                            const result = await buildOidcAuthorizeUrl({
                                tenantSlug: sso.tenantSlug,
                                provider: 'oidc',
                                redirectUri: `${window.location.origin}/signin-oidc`,
                                loginHint: trimmed,
                            })
                            if (result.authorizeUrl) {
                                sessionStorage.setItem('sso.tenantSlug', sso.tenantSlug)
                                sessionStorage.setItem('sso.provider', 'oidc')
                                sessionStorage.setItem('sso.redirect', initialRedirect)
                                window.location.href = result.authorizeUrl
                                return
                            }
                            // OIDC URL build failed — fall back to credentials.
                            setStage({ phase: 'email' })
                            setError({ message: "Couldn't start SSO sign-in." })
                            return
                        }
                        if (providers.includes('saml')) {
                            setStage({ phase: 'sso-redirecting', tenantName: sso.tenantName ?? null })
                            const result = await buildSamlRedirectUrl({
                                tenantSlug: sso.tenantSlug,
                                redirect: initialRedirect,
                            })
                            if (result.redirectUrl) {
                                window.location.href = result.redirectUrl
                                return
                            }
                            setStage({ phase: 'email' })
                            setError({ message: "Couldn't start SAML sign-in." })
                            return
                        }
                    }
                    didSsoRedirect = false
                } catch {
                    // SSO resolve failure is non-fatal — fall through to
                    // credentials. The user shouldn't be blocked by an
                    // unrelated SSO check error.
                }

                if (didSsoRedirect) return

                // No SSO — acquire locate token + advance to next step.
                let locateToken: string | null = null
                let brandingOverlay: { tenantName: string; logoUrl: string } | null = null
                let orgChoices: { region: string; label?: string }[] = []
                try {
                    const locateResult = await apiLocate(trimmed)
                    if (isLocateChallenge(locateResult)) {
                        setError({ message: 'Additional verification required. Please retry.' })
                        return
                    }
                    const ok = locateResult as LocateResponseOk
                    locateToken = ok.locateToken
                    if (ok.orgPicker && ok.orgChoices && ok.orgChoices.length > 1) {
                        orgChoices = ok.orgChoices
                    }
                    if (ok.branding && !tenantLogoUrl) {
                        brandingOverlay = {
                            tenantName: ok.branding.tenantName,
                            logoUrl: ok.branding.logoUrl,
                        }
                        setTenantLogoUrl(ok.branding.logoUrl)
                        setTenantName(ok.branding.tenantName)
                    } else if (tenantLogoUrl && tenantName) {
                        brandingOverlay = { tenantName, logoUrl: tenantLogoUrl }
                    }
                } catch (err) {
                    // Surface the locate error explicitly rather than
                    // sliding into the password step with a null token —
                    // that produces the misleading "Sign-in session
                    // expired" downstream. Stay on the email step so the
                    // user can retry.
                    const apiErr = err as { message?: string; code?: string }
                    setError({
                        message: apiErr?.message ?? "Couldn't reach the sign-in service. Please try again.",
                    })
                    return
                }

                if (!locateToken) {
                    setError({ message: "Sign-in is unavailable right now. Please try again shortly." })
                    return
                }

                // Multi-region match → user must pick which region's
                // account they're signing into. Single-region match
                // skips the picker and goes straight to the password
                // step (the locate token already carries the only
                // valid region).
                if (orgChoices.length > 1) {
                    setStage({
                        phase: 'org-picker',
                        email: trimmed,
                        locateToken,
                        choices: orgChoices,
                    })
                    return
                }

                setStage({
                    phase: 'password',
                    email: trimmed,
                    locateToken,
                    branding: brandingOverlay,
                })
            } finally {
                setResolving(false)
            }
        },
        [email, initialRedirect, tenantLogoUrl, tenantName],
    )

    // ─── Password submit ─────────────────────────────────────

    const submitPassword = useCallback(
        async (locateToken: string | null, region: string | undefined) => {
            if (!locateToken) {
                setError({ message: 'Sign-in session expired. Start over.' })
                return false
            }
            try {
                const result = await apiAuthenticate(locateToken, password, region)
                if (!result.ok) {
                    setError({
                        message: "That email and password don't match. Try again, or reset your password below.",
                    })
                    return false
                }
                if (result.mfaRequired) {
                    mfaReturnFocusRef.current = document.activeElement as HTMLElement | null
                    setMfaCode('')
                    setStage({
                        phase: 'mfa',
                        mfaToken: result.mfaToken,
                        branding: tenantLogoUrl && tenantName ? { tenantName, logoUrl: tenantLogoUrl } : null,
                    })
                    setMfaModalOpen(true)
                    return true
                }
                if (result.mustChangePassword) {
                    setStage({ phase: 'force-change', redirectAfter: initialRedirect })
                    return true
                }
                setStage({ phase: 'done', redirectAfter: initialRedirect })
                window.location.href = initialRedirect
                return true
            } catch {
                setError({ message: 'Network error during sign-in.' })
                return false
            }
        },
        [password, initialRedirect, tenantLogoUrl, tenantName],
    )

    const onPasswordSubmit = useCallback(
        async (e: SyntheticEvent<HTMLFormElement>) => {
            e.preventDefault()
            setError(null)
            setBusy(true)
            if (stage.phase === 'password') {
                await submitPassword(stage.locateToken, stage.region)
            }
            setBusy(false)
        },
        [stage, submitPassword],
    )

    const onPickOrg = useCallback(
        (region: string) => {
            if (stage.phase !== 'org-picker') return
            setStage({
                phase: 'password',
                email: stage.email,
                locateToken: stage.locateToken,
                region,
                branding: tenantLogoUrl && tenantName
                    ? { tenantName, logoUrl: tenantLogoUrl }
                    : null,
            })
        },
        [stage, tenantLogoUrl, tenantName],
    )

    const onUseDifferentEmail = useCallback(() => {
        setStage({ phase: 'email' })
        setPassword('')
        setError(null)
        setTenantLogoUrl(null)
        setTenantName(null)
    }, [])

    // ─── MFA modal submit ────────────────────────────────────

    const onMfaSubmit = useCallback(
        async (e: SyntheticEvent<HTMLFormElement>) => {
            e.preventDefault()
            if (!mfaCode.trim()) {
                setError({ message: 'Enter the 6-digit code from your authenticator app.' })
                return
            }
            if (stage.phase !== 'mfa') return
            setMfaSubmitting(true)
            setError(null)
            try {
                const result = await apiVerifyMfa(stage.mfaToken, mfaCode)
                if (!result.ok) {
                    setError({
                        message: "That code didn't work. Try the latest one from your authenticator app.",
                    })
                    setMfaSubmitting(false)
                    return
                }
                if (result.mustChangePassword) {
                    setMfaModalOpen(false)
                    setStage({ phase: 'force-change', redirectAfter: initialRedirect })
                    setMfaSubmitting(false)
                    return
                }
                setStage({ phase: 'done', redirectAfter: initialRedirect })
                window.location.href = initialRedirect
            } catch {
                setError({ message: 'Network error during MFA verification.' })
                setMfaSubmitting(false)
            }
        },
        [mfaCode, stage, initialRedirect],
    )

    const onMfaCancel = useCallback(() => {
        setMfaModalOpen(false)
        setMfaCode('')
        setMfaSubmitting(false)
        setBusy(false)
        setStage({ phase: 'email' })
        const target = mfaReturnFocusRef.current
        if (target && typeof target.focus === 'function') {
            window.requestAnimationFrame(() => target.focus())
        }
    }, [])

    // ─── Force-password-change submit ────────────────────────

    const onForceChangeSubmit = useCallback(
        async (e: SyntheticEvent<HTMLFormElement>) => {
            e.preventDefault()
            // Pre-flight matches Edge/Program.cs Identity defaults (8 chars,
            // upper, lower, digit, symbol). Server returns specific errors
            // (PasswordRequiresDigit, PasswordTooShort, …) for the *actual*
            // policy on submit — the admin UI can tighten or loosen the
            // requirements at runtime, so these checks are best-effort UX,
            // not the source of truth.
            const policyError = validatePasswordPolicy(newPassword)
            if (policyError) {
                setError({ message: policyError })
                return
            }
            if (newPassword !== confirmPassword) {
                setError({ message: "Passwords don't match." })
                return
            }
            setPasswordSaving(true)
            setError(null)
            const result = await changePassword(newPassword)
            if (result.ok) {
                window.location.href = initialRedirect
                return
            }
            setError({ message: result.message ?? 'Failed to change password.' })
            setPasswordSaving(false)
        },
        [newPassword, confirmPassword, initialRedirect],
    )

    // Escape hatch from the force-change screen. The session is established
    // (the user authenticated) but they can't proceed without rotating the
    // password — if they hit this by mistake, "Sign out" rotates the
    // SecurityStamp + clears the cookie so the next /login is a clean start.
    const onForceChangeSignOut = useCallback(async () => {
        try { await apiLogout() } catch { /* best-effort */ }
        window.location.replace('/login')
    }, [])

    useEffect(() => {
        if (stage.phase === 'force-change') {
            const id = window.setTimeout(() => newPasswordRef.current?.focus(), 0)
            return () => window.clearTimeout(id)
        }
        return
    }, [stage.phase])

    // ─── Render ──────────────────────────────────────────────

    const titleText =
        stage.phase === 'force-change' ? 'Change your password' :
        stage.phase === 'sso-redirecting' ? 'Redirecting…' :
        stage.phase === 'org-picker' ? 'Choose where to sign in' :
        'Sign in'
    const subtitleText =
        stage.phase === 'force-change'
            ? 'Your account is using a temporary password. Set a new password before continuing.'
            : stage.phase === 'org-picker'
                ? 'Your email matches accounts in more than one place. Pick which one you want to use.'
                : 'Welcome back to Inspire'

    return (
        <div className="min-h-screen flex flex-col items-center justify-center relative px-4">
            <AnimatedBackground />
            <GridOverlay />

            <div className="w-full max-w-[400px] relative">
                <div className="flex justify-center mb-10">
                    <AnimatePresence mode="wait" initial={false}>
                        {tenantLogoUrl && (stage.phase === 'password' || stage.phase === 'mfa') ? (
                            <motion.img
                                key="tenant-logo"
                                src={tenantLogoUrl}
                                alt={tenantName ?? 'Tenant logo'}
                                initial={{ opacity: 0, y: 4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -4 }}
                                transition={{ duration: 0.22, ease: 'easeOut' }}
                                className="max-h-12 max-w-[220px] object-contain drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]"
                                onError={() => setTenantLogoUrl(null)}
                            />
                        ) : (
                            <motion.img
                                key={isDark ? 'brand-logo-dark' : 'brand-logo-light'}
                                src={isDark ? '/brand/optymyse-neg.svg' : '/brand/optymyse-pos.svg'}
                                alt="Optymyse"
                                initial={{ opacity: 0, y: 4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -4 }}
                                transition={{ duration: 0.22, ease: 'easeOut' }}
                                className="h-9 drop-shadow-[0_2px_12px_rgba(140,40,100,0.18)] dark:drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]"
                            />
                        )}
                    </AnimatePresence>
                </div>

                <motion.div
                    layout
                    transition={{ type: 'spring', stiffness: 380, damping: 34, mass: 0.7 }}
                    className="rounded-2xl border border-foreground/[0.06] dark:border-white/[0.08] bg-white/75 dark:bg-black/35 backdrop-blur-2xl shadow-[0_12px_48px_-16px_rgba(140,40,100,0.22),0_2px_8px_-4px_rgba(80,60,180,0.10)] dark:shadow-[0_8px_40px_-12px_rgba(0,0,0,0.5)] p-8 sm:p-10 overflow-hidden"
                >
                    <div className="mb-7">
                        <h1 className="text-[1.375rem] font-semibold tracking-tight text-foreground">
                            {titleText}
                        </h1>
                        <div className="text-[0.8125rem] text-muted-foreground mt-1 min-h-[1.25rem]">
                            <AnimatePresence mode="wait" initial={false}>
                                {stage.phase === 'password' ? (
                                    <motion.div
                                        key="identity-line"
                                        initial={{ opacity: 0, y: 4 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -4 }}
                                        transition={{ duration: 0.18, ease: 'easeOut' }}
                                        className="flex items-center gap-1.5 min-w-0"
                                    >
                                        <span className="truncate text-foreground/80 font-medium">{stage.email}</span>
                                        <span aria-hidden="true" className="text-muted-foreground/40">·</span>
                                        <button
                                            type="button"
                                            onClick={onUseDifferentEmail}
                                            className="shrink-0 text-muted-foreground underline decoration-muted-foreground/40 decoration-1 underline-offset-[3px] hover:text-foreground hover:decoration-foreground transition-colors cursor-pointer"
                                        >
                                            Change
                                        </button>
                                    </motion.div>
                                ) : (
                                    <motion.p
                                        key="subtitle"
                                        initial={{ opacity: 0, y: 4 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -4 }}
                                        transition={{ duration: 0.18, ease: 'easeOut' }}
                                    >
                                        {subtitleText}
                                    </motion.p>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>

                    {stage.phase === 'loading' && (
                        <div className="flex justify-center py-6">
                            <svg className="animate-spin h-5 w-5 text-muted-foreground" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                        </div>
                    )}

                    {stage.phase === 'sso-redirecting' && (
                        <div className="flex flex-col items-center justify-center py-6 text-center">
                            <svg className="animate-spin h-5 w-5 text-muted-foreground mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            <p className="text-[0.8125rem] text-foreground">
                                {stage.tenantName ? `Redirecting to ${stage.tenantName}…` : 'Redirecting to your provider…'}
                            </p>
                        </div>
                    )}

                    {stage.phase === 'org-picker' && (
                        <div className="space-y-2">
                            <AnimatePresence initial={false}>
                                {error && (
                                    <motion.div
                                        key="error"
                                        role="alert"
                                        initial={{ opacity: 0, y: -6, height: 0, marginBottom: 0 }}
                                        animate={{ opacity: 1, y: 0, height: 'auto', marginBottom: 12 }}
                                        exit={{ opacity: 0, y: -6, height: 0, marginBottom: 0 }}
                                        transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                                        className="flex items-start gap-2.5 rounded-lg border border-destructive/20 bg-destructive/[0.06] px-3.5 py-2.5 text-[0.8125rem] text-destructive overflow-hidden"
                                    >
                                        <span className="leading-relaxed">{error.message}</span>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                            {stage.choices.map((choice) => {
                                const region = choice.region
                                const tenantName = (choice.label ?? '').trim()
                                const region_friendly = regionLabel(region)
                                return (
                                    <button
                                        key={region + ':' + (tenantName || '')}
                                        type="button"
                                        onClick={() => onPickOrg(region)}
                                        className="w-full text-left rounded-lg border border-foreground/[0.08] bg-white/40 dark:bg-white/[0.03] hover:bg-white/70 dark:hover:bg-white/[0.06] hover:border-foreground/[0.14] transition-colors p-3.5 flex items-center justify-between gap-3 cursor-pointer focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/20 focus-visible:border-ring"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="text-[0.875rem] font-medium text-foreground truncate">
                                                {tenantName || region_friendly}
                                            </div>
                                            {tenantName && (
                                                <div className="text-[0.75rem] text-muted-foreground mt-0.5 truncate">
                                                    {region_friendly}
                                                </div>
                                            )}
                                        </div>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                            className="h-4 w-4 text-muted-foreground/60 shrink-0"
                                            aria-hidden="true"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </button>
                                )
                            })}
                            <button
                                type="button"
                                onClick={onUseDifferentEmail}
                                className="w-full mt-3 text-[0.75rem] text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors cursor-pointer"
                            >
                                Use a different email
                            </button>
                        </div>
                    )}

                    {(stage.phase === 'email' || stage.phase === 'password') && (
                        <form onSubmit={stage.phase === 'email' ? onEmailSubmit : onPasswordSubmit}>
                            <AnimatePresence initial={false}>
                                {error && (
                                    <motion.div
                                        key="error"
                                        role="alert"
                                        initial={{ opacity: 0, y: -6, height: 0, marginBottom: 0 }}
                                        animate={{ opacity: 1, y: 0, height: 'auto', marginBottom: 16 }}
                                        exit={{ opacity: 0, y: -6, height: 0, marginBottom: 0 }}
                                        transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                                        className="flex items-start gap-2.5 rounded-lg border border-destructive/20 bg-destructive/[0.06] px-3.5 py-2.5 text-[0.8125rem] text-destructive overflow-hidden"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 mt-0.5 shrink-0">
                                            <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 6a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 6Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd" />
                                        </svg>
                                        <span className="leading-relaxed">{error.message}</span>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <AnimatePresence mode="wait" initial={false}>
                                {stage.phase === 'email' ? (
                                    <motion.div
                                        key="email-field"
                                        initial={{ opacity: 0, x: -12 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -12 }}
                                        transition={{ duration: 0.18, ease: 'easeOut' }}
                                    >
                                        <label htmlFor="email" className="block text-[0.8125rem] font-medium text-foreground mb-1.5">
                                            Email
                                        </label>
                                        <input
                                            id="email"
                                            type="email"
                                            autoComplete="email"
                                            autoFocus
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="you@company.com"
                                            className="flex h-10 w-full rounded-lg border border-foreground/[0.08] bg-white/60 dark:bg-white/[0.06] px-3.5 text-sm text-foreground placeholder:text-muted-foreground/40 transition-all focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/20 focus-visible:border-ring backdrop-blur-sm"
                                        />
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="password-field"
                                        initial={{ opacity: 0, x: 12 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: 12 }}
                                        transition={{ duration: 0.18, ease: 'easeOut' }}
                                    >
                                        <div className="flex items-center justify-between mb-1.5">
                                            <label htmlFor="password" className="block text-[0.8125rem] font-medium text-foreground">
                                                Password
                                            </label>
                                            {emailEnabled && (
                                                <a
                                                    href="/forgot-password"
                                                    className="text-[0.75rem] text-muted-foreground underline underline-offset-4 hover:text-foreground"
                                                >
                                                    Forgot password?
                                                </a>
                                            )}
                                        </div>
                                        <input
                                            id="password"
                                            type="password"
                                            autoComplete="current-password"
                                            autoFocus
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="flex h-10 w-full rounded-lg border border-foreground/[0.08] bg-white/60 dark:bg-white/[0.06] px-3.5 text-sm text-foreground placeholder:text-muted-foreground/40 transition-all focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/20 focus-visible:border-ring backdrop-blur-sm"
                                        />
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <div className="mt-6">
                                <button
                                    type="submit"
                                    disabled={busy || resolving}
                                    className="w-full h-10 rounded-lg text-[0.8125rem] font-medium shadow-md shadow-primary/20 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                                >
                                    {(busy || resolving) && (
                                        <svg className="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                    )}
                                    {(busy || resolving)
                                        ? (stage.phase === 'email' ? 'Checking…' : 'Signing in…')
                                        : (stage.phase === 'email' ? 'Continue' : 'Sign in')}
                                </button>
                            </div>
                        </form>
                    )}

                    {stage.phase === 'force-change' && (
                        <form onSubmit={onForceChangeSubmit}>
                            {error && (
                                <div className="mb-4 rounded-md border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                                    {error.message}
                                </div>
                            )}
                            <div className="space-y-4">
                                <div>
                                    <label htmlFor="newPassword" className="block text-[0.8125rem] font-medium text-foreground mb-1.5">
                                        New password
                                    </label>
                                    <input
                                        ref={newPasswordRef}
                                        id="newPassword"
                                        type="password"
                                        autoComplete="new-password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        className="flex h-10 w-full rounded-lg border border-foreground/[0.08] bg-white/60 dark:bg-white/[0.06] px-3.5 text-sm text-foreground placeholder:text-muted-foreground/40 transition-all focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/20 focus-visible:border-ring backdrop-blur-sm"
                                    />
                                    <p className="text-[0.6875rem] text-muted-foreground mt-1.5">
                                        {PASSWORD_POLICY_HINT}
                                    </p>
                                </div>
                                <div>
                                    <label htmlFor="confirmPassword" className="block text-[0.8125rem] font-medium text-foreground mb-1.5">
                                        Confirm password
                                    </label>
                                    <input
                                        id="confirmPassword"
                                        type="password"
                                        autoComplete="new-password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="flex h-10 w-full rounded-lg border border-foreground/[0.08] bg-white/60 dark:bg-white/[0.06] px-3.5 text-sm text-foreground placeholder:text-muted-foreground/40 transition-all focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/20 focus-visible:border-ring backdrop-blur-sm"
                                    />
                                </div>
                            </div>
                            <div className="mt-6 space-y-3">
                                <button
                                    type="submit"
                                    disabled={passwordSaving || !newPassword || !confirmPassword}
                                    className="w-full h-10 rounded-lg text-[0.8125rem] font-medium shadow-md shadow-primary/20 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                                >
                                    {passwordSaving && (
                                        <svg className="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                    )}
                                    {passwordSaving ? 'Updating password…' : 'Update password'}
                                </button>
                                <button
                                    type="button"
                                    onClick={onForceChangeSignOut}
                                    className="w-full text-[0.8125rem] text-muted-foreground underline underline-offset-4 hover:text-foreground cursor-pointer"
                                >
                                    Sign out
                                </button>
                            </div>
                        </form>
                    )}
                </motion.div>

                <div className="mt-8 flex flex-col items-center gap-4">
                    <div className="text-center space-y-1 dark:drop-shadow-[0_1px_4px_rgba(0,0,0,0.3)]">
                        <p className="text-[0.75rem] text-foreground/55 dark:text-white/70">
                            Optymyse Inspire · Visual Communications
                        </p>
                        <p className="text-[0.6875rem] text-foreground/40 dark:text-white/50">
                            © {new Date().getFullYear()} Optymyse Ltd. All rights reserved.
                        </p>
                    </div>
                </div>
            </div>

            {mfaModalOpen && stage.phase === 'mfa' && (
                <MfaCodeModal
                    code={mfaCode}
                    error={error?.message ?? null}
                    submitting={mfaSubmitting}
                    onCodeChange={setMfaCode}
                    onSubmit={onMfaSubmit}
                    onCancel={onMfaCancel}
                />
            )}
            {/* Hidden field to consume redirectHashKey/initialRedirect — keeps
                the linter happy when the prop is unused in non-redirect paths.
                Both are used by the navigate paths above. */}
            <span className="hidden" data-redirect-hash={redirectHashKey(initialRedirect)} />
        </div>
    )
}

// ─── MFA modal ────────────────────────────────────────────────

function MfaCodeModal({
    code,
    error,
    submitting,
    onCodeChange,
    onSubmit,
    onCancel,
}: {
    code: string
    error: string | null
    submitting: boolean
    onCodeChange: (v: string) => void
    onSubmit: (e: SyntheticEvent<HTMLFormElement>) => void
    onCancel: () => void
}) {
    const inputRef = useRef<HTMLInputElement>(null)
    useEffect(() => {
        inputRef.current?.focus()
    }, [])

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.15 }}
                className="w-full max-w-[380px] rounded-2xl border border-white/[0.12] dark:border-white/[0.08] bg-white/95 dark:bg-black/85 backdrop-blur-2xl shadow-2xl p-7"
            >
                <h2 className="text-[1.0625rem] font-semibold tracking-tight text-foreground">
                    Two-factor code
                </h2>
                <p className="text-[0.8125rem] text-muted-foreground mt-1">
                    Enter the 6-digit code from your authenticator app.
                </p>
                <form onSubmit={onSubmit} className="mt-5">
                    {error && (
                        <div className="mb-3 rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-[0.8125rem] text-destructive">
                            {error}
                        </div>
                    )}
                    <input
                        ref={inputRef}
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        value={code}
                        onChange={(e) => onCodeChange(e.target.value)}
                        placeholder="123 456"
                        className="flex h-12 w-full rounded-lg border border-foreground/[0.08] bg-white/60 dark:bg-white/[0.06] px-3.5 text-center font-mono text-lg tracking-[0.3em] text-foreground placeholder:text-muted-foreground/40 transition-all focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/20 focus-visible:border-ring backdrop-blur-sm"
                    />
                    <div className="mt-5 flex flex-col gap-2">
                        <button
                            type="submit"
                            disabled={submitting || !code.trim()}
                            className="w-full h-10 rounded-lg text-[0.8125rem] font-medium shadow-md shadow-primary/20 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                        >
                            {submitting ? 'Verifying…' : 'Verify'}
                        </button>
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={submitting}
                            className="w-full text-[0.8125rem] text-muted-foreground underline underline-offset-4 hover:text-foreground disabled:opacity-60 cursor-pointer"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </motion.div>
        </div>
    )
}
